import L from 'leaflet';

// Constants - use relative paths for API to work with any domain
export const API = '/api';

export const ORANGELABEL_LOGO = "/logos/orangelabel-96.png";
export const BTCMAP_LOGO = "https://btcmap.org/images/logo.svg";
export const ABIB_LOGO = "/logos/abib-96.png";
export const MEETUP_LOGO = "/logos/meetup.svg";

// Detect source platform of an event/group for icon selection. We prefer
// the explicit `platform` field (set by create_meetup_group +
// detect_platform_from_url) but fall back to URL sniffing for records
// that predate the field.
export const getEventPlatform = (item) => {
  const p = (item && item.platform) ? String(item.platform).toLowerCase() : '';
  if (p) return p;
  const url = (item && (item.event_url || item.url || item.meetup_url)) || '';
  const lower = url.toLowerCase();
  if (lower.includes('bitcoinwalk')) return 'bitcoin_walk';
  if (lower.includes('facebook.com') || lower.includes('fb.com') || lower.includes('fb.me')) return 'facebook';
  if (lower.includes('meetup.com')) return 'meetup';
  if (lower.includes('lu.ma') || lower.includes('luma.com')) return 'luma';
  if (lower.includes('eventbrite')) return 'eventbrite';
  if (lower.includes('satlantis.io')) return 'satlantis';
  return '';
};

export const QUICK_CATEGORIES = ['All', 'Bitcoin Service', 'Retail', 'Restaurant', 'Cafe', 'IT / Dev', 'Accounting', 'ATM'];

export const REGION_COORDS = {
  'Australia-Wide': [-25.2744, 133.7751], 'NSW': [-33.8688, 151.2093], 'Victoria': [-37.8136, 144.9631],
  'Queensland': [-27.4705, 153.0260], 'South Australia': [-34.9285, 138.6007], 'Tasmania': [-42.8821, 147.3272],
  'Western Australia': [-31.9505, 115.8605], 'Northern Territory': [-12.4634, 130.8456], 'ACT': [-35.2809, 149.1300],
  'Global': [-25.2744, 133.7751], 'Australia': [-25.2744, 133.7751], 'VIC': [-37.8136, 144.9631],
  'QLD': [-27.4705, 153.0260], 'SA': [-34.9285, 138.6007], 'WA': [-31.9505, 115.8605],
  'NT': [-12.4634, 130.8456], 'TAS': [-42.8821, 147.3272],
  'New Zealand': [-40.9006, 174.886], 'NZ': [-40.9006, 174.886]
};

// State bounds for service region matching
export const STATE_BOUNDS = {
  'NSW': L.latLngBounds(L.latLng(-37.5, 141), L.latLng(-28, 154)),
  'Victoria': L.latLngBounds(L.latLng(-39, 141), L.latLng(-34, 150)),
  'VIC': L.latLngBounds(L.latLng(-39, 141), L.latLng(-34, 150)),
  'Queensland': L.latLngBounds(L.latLng(-29, 138), L.latLng(-10, 154)),
  'QLD': L.latLngBounds(L.latLng(-29, 138), L.latLng(-10, 154)),
  'South Australia': L.latLngBounds(L.latLng(-38, 129), L.latLng(-26, 141)),
  'SA': L.latLngBounds(L.latLng(-38, 129), L.latLng(-26, 141)),
  'Western Australia': L.latLngBounds(L.latLng(-35, 113), L.latLng(-14, 129)),
  'WA': L.latLngBounds(L.latLng(-35, 113), L.latLng(-14, 129)),
  'Tasmania': L.latLngBounds(L.latLng(-44, 144), L.latLng(-40, 149)),
  'TAS': L.latLngBounds(L.latLng(-44, 144), L.latLng(-40, 149)),
  'Northern Territory': L.latLngBounds(L.latLng(-26, 129), L.latLng(-11, 138)),
  'NT': L.latLngBounds(L.latLng(-26, 129), L.latLng(-11, 138)),
  'ACT': L.latLngBounds(L.latLng(-35.9, 148.7), L.latLng(-35.1, 149.4)),
  'Greater Sydney': L.latLngBounds(L.latLng(-34.2, 150.5), L.latLng(-33.4, 151.5)),
  'South East Queensland': L.latLngBounds(L.latLng(-28.5, 152), L.latLng(-26, 154)),
  'Sunshine Coast': L.latLngBounds(L.latLng(-27, 152.5), L.latLng(-26, 153.5)),
  'New Zealand': L.latLngBounds(L.latLng(-47, 166), L.latLng(-34, 179)),
  'NZ': L.latLngBounds(L.latLng(-47, 166), L.latLng(-34, 179))
};

// Helper functions
export const isATM = (b) => 
  (b.category || []).some(c => c.toLowerCase().includes('atm')) || 
  (b.business_name || '').toLowerCase().includes('atm');

export const matchesCategory = (b, cat) => {
  if (cat === 'All') return true;
  if (cat === 'ATM') return isATM(b);
  const cats = b.category || [];
  const mappings = {
    'bitcoin service': ['bitcoin service', 'bitcoin'],
    'retail': ['retail', 'store', 'shop'],
    'restaurant': ['restaurant', 'food'],
    'cafe': ['cafe', 'coffee'],
    'it / dev': ['information technology', 'development', 'it', 'software'],
    'accounting': ['accounting', 'tax']
  };
  const m = mappings[cat.toLowerCase()] || [cat.toLowerCase()];
  return cats.some(c => m.some(x => c.toLowerCase().includes(x)));
};

export const formatWebsiteUrl = (url) => {
  if (!url) return '';
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
};

export const getWebsiteHref = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `https://${url}`;
};

// Format event time — `timeStr` is an "HH:MM" string in the event venue's
// **local** time (Meetup returns dateTime with offset, the backend splits
// on 'T' and keeps only the local HH:MM; HTML/iCal fallbacks do the same).
// Previously this function added an Australian-state UTC offset, which
// double-shifted already-local times (e.g. Sydney 16:00 → 03:00 next day).
// The `state` arg is kept for call-site compatibility but ignored.
export const formatEventTime = (timeStr /* , state */) => {
  if (!timeStr) return null;
  const [hStr, mStr] = timeStr.split(':');
  const hours = Number(hStr);
  const minutes = Number(mStr);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : (hours > 12 ? hours - 12 : hours);
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

export const getMarkerCoords = (b) => {
  if (b.latitude && b.longitude) return [b.latitude, b.longitude];
  if (!b.btc_map && b.based_state) return REGION_COORDS[b.based_state] || REGION_COORDS['Australia-Wide'];
  return REGION_COORDS[b.region] || REGION_COORDS['Australia-Wide'];
};

// Country-code → human country name (UN-5 scheme has a sibling name file).
// Inlined to the most common codes — full mapping lives in
// @/data/countryNames but MapHelpers runs on every map render so we keep
// it small here. Missing codes fall through to the ISO2 code itself.
const _CC_NAME = {
  AU: 'Australia', NZ: 'New Zealand', US: 'United States', CA: 'Canada',
  GB: 'United Kingdom', UK: 'United Kingdom', DE: 'Germany', FR: 'France',
  JP: 'Japan', CN: 'China', IN: 'India', SG: 'Singapore', MY: 'Malaysia',
  TH: 'Thailand', ID: 'Indonesia', PH: 'Philippines', KR: 'South Korea',
  BR: 'Brazil', MX: 'Mexico', AR: 'Argentina', CL: 'Chile', CO: 'Colombia',
  ZA: 'South Africa', KE: 'Kenya', NG: 'Nigeria', GH: 'Ghana', UG: 'Uganda',
  EG: 'Egypt', MA: 'Morocco', TN: 'Tunisia', TZ: 'Tanzania',
  ES: 'Spain', IT: 'Italy', NL: 'Netherlands', BE: 'Belgium', CH: 'Switzerland',
  SE: 'Sweden', NO: 'Norway', DK: 'Denmark', FI: 'Finland', IE: 'Ireland',
  AT: 'Austria', PT: 'Portugal', PL: 'Poland', GR: 'Greece', CZ: 'Czechia',
  RU: 'Russia', UA: 'Ukraine', TR: 'Turkey', IL: 'Israel', AE: 'UAE',
  SA: 'Saudi Arabia', HK: 'Hong Kong', TW: 'Taiwan', VN: 'Vietnam',
};

export const getFullAddress = (b) => {
  if (b.full_address) return b.full_address;

  // BTCMap import: parse trailing address from the synthesized note.
  if (b.btc_map && b.notes) {
    const m = b.notes.match(/Imported from BTCMap\.org - (.+)/);
    if (m?.[1]) return m[1];
  }

  // Compose from canonical address fields if any are populated.
  const cc = (b.country_code || '').toUpperCase();
  const parts = [b.addr_suburb, b.addr_city, b.addr_state, _CC_NAME[cc] || cc]
    .filter(Boolean);
  if (parts.length) return parts.join(', ');

  // Last-resort legacy region fallback — BUT guard against the BTCMap
  // importer's stale "Australia" default when country_code contradicts it.
  // Joylove Cyber (KE) was showing "Australia" before this guard.
  if (b.region && (!cc || cc === 'AU' || (b.region || '').toLowerCase() !== 'australia')) {
    return b.region;
  }

  // Fall through to country name if we have a code
  return _CC_NAME[cc] || cc || '';
};

// Human-readable scope label for a business card.
//
// Legacy `b.region` holds free-text ("Australia-Wide", "NSW", "global") and
// was removed from OL records on 2026-04-17. For OL records we now derive
// the label from the canonical scope fields. BTCMap imports still use the
// legacy full-address path via getFullAddress.
const _UN_REGION_NAMES = {
  oceania: 'Oceania', asia: 'Asia', americas: 'Americas',
  europe: 'Europe',   africa: 'Africa',
};

// ISO 3166-2 → human state name for the common codes. Used by
// getDisplayRegion to render scope=state records with a readable label
// (e.g. "Western Australia" instead of "AU-WA").
const _STATE_NAMES = {
  'AU-NSW': 'New South Wales', 'AU-VIC': 'Victoria',
  'AU-QLD': 'Queensland',      'AU-SA':  'South Australia',
  'AU-WA':  'Western Australia','AU-TAS': 'Tasmania',
  'AU-NT':  'Northern Territory', 'AU-ACT': 'Australian Capital Territory',
  'NZ-AUK': 'Auckland',        'NZ-WGN': 'Wellington',
  'NZ-CAN': 'Canterbury',      'NZ-OTA': 'Otago',
  'NZ-WKO': 'Waikato',         'NZ-BOP': 'Bay of Plenty',
};

export const getDisplayRegion = (b) => {
  if (b.btc_map) return getFullAddress(b);

  // Service-scope based label (new canonical source). Each branch prefers
  // a human-readable name and falls back to the raw code or a generic word.
  const ccName = (cc) => _CC_NAME[(cc || '').toUpperCase()] || cc || '';
  const stateName = (code) => _STATE_NAMES[(code || '').toUpperCase()] || code || '';

  switch (b.service_scope) {
    case 'global':
      return 'Global';
    case 'region':
      return _UN_REGION_NAMES[(b.service_region || '').toLowerCase()] || 'Region';
    case 'country':
      return ccName(b.service_country || b.country_code) || 'Country';
    case 'state': {
      const s = stateName(b.service_state || b.based_state);
      const c = ccName(b.service_country || b.country_code);
      if (s && c) return `${s}, ${c}`;
      return s || c || 'State';
    }
    case 'county': {
      const county = b.service_county;
      const s = stateName(b.service_state);
      if (county && s) return `${county}, ${s}`;
      return county || s || 'County';
    }
    default:
      break;
  }
  // Fallback to legacy region string for any record not yet migrated.
  return b.region || '';
};

// Check if business should be visible based on map bounds.
//
// The sidebar lists whatever passes this predicate. Global-scope records
// have no coords and no region; they service every viewport — always return
// true for them. Otherwise apply the region/state/coord bounds filter.
export const isBusinessVisible = (b, bounds, stateBounds) => {
  if (!bounds) return true;

  // Global-scope: no geography to bound against — always visible.
  const isGlobal =
    b.service_scope === 'global' ||
    b.service_area === 'global' ||
    (b.region || '').toLowerCase() === 'global';
  if (isGlobal) return true;

  // Legacy Australia-Wide / Australia text regions: show whenever Australia bounds intersect.
  if (!b.btc_map && (b.region === 'Australia-Wide' || b.region === 'Australia')) {
    const australiaBounds = L.latLngBounds(L.latLng(-45, 110), L.latLng(-10, 155));
    if (bounds.intersects(australiaBounds)) return true;
  }

  // Service-region (legacy state name) match
  if (!b.btc_map && b.region && stateBounds[b.region]) {
    if (bounds.intersects(stateBounds[b.region])) return true;
  }

  // Default: marker coord inside viewport
  const coords = getMarkerCoords(b);
  return coords && bounds.contains(L.latLng(coords[0], coords[1]));
};

// Build a Google Maps directions URL for a business. Prefers real coords
// (latitude/longitude or BTCMap osm_lat/osm_lon); otherwise falls back to the
// human-readable scope label. The URL is constructed by us (no untrusted
// scheme), so it does not need safeHref.
export const getDirectionsUrl = (business) => {
  const lat = business.latitude || business.osm_lat;
  const lng = business.longitude || business.osm_lon;
  if (lat && lng) {
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(getDisplayRegion(business))}`;
};

// Open Google Maps directions for a business in a new tab. noopener,noreferrer
// prevents the opened tab from accessing window.opener.
export const openDirections = (business) => {
  window.open(getDirectionsUrl(business), '_blank', 'noopener,noreferrer');
};

// Match category using map tags
export const matchesCategoryOrTag = (b, tagName, mapTags) => {
  if (tagName === 'All') return true;
  
  const tag = mapTags.find(t => t.name === tagName);
  if (!tag) return matchesCategory(b, tagName);
  
  if (tag.categories && tag.categories.length > 0) {
    return tag.categories.some(catName => matchesCategory(b, catName));
  }
  
  return matchesCategory(b, tagName);
};
